import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Code,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  CONFIG_REGISTRY,
  type BusinessRuleConfigStatus,
  type BusinessRuleType,
} from '@prepzio/shared';
import { IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { InfoTip } from '../components/InfoTip';
import { SeededLock, SEEDED_FILES } from '../components/SeededLock.js';
import { useBusinessRules, useReviewBusinessRule } from '../hooks/use-product-document';
import { useT, type MessageKey, type TranslateFn } from '../i18n';
import type { AdminBusinessRule } from '../lib/api';
import { errorMessage } from '../lib/ui';

const PRIORITY_COLOR: Record<string, string> = { P0: 'red', P1: 'orange', P2: 'gray' };

/**
 * `product` vs `platform` — which question the rule answers.
 *
 * The catalog holds both because the plumbing is where the product promise is
 * actually kept, but they are read by different people: a product owner looking
 * for the scoring formula should not scroll past request-id handling to reach it.
 *
 * Only the colour is fixed here; the label and the hint are message keys, so the
 * two never drift apart in one language and not the other.
 */
const TYPE_COLOR: Record<BusinessRuleType, string> = {
  product: 'teal',
  platform: 'indigo',
};

const typeMeta = (t: TranslateFn, type: BusinessRuleType) => ({
  label: t(`br.type.${type}` as MessageKey),
  hint: t(`br.type.${type}Hint` as MessageKey),
  color: TYPE_COLOR[type],
});

const typeOptions = (t: TranslateFn) =>
  (Object.keys(TYPE_COLOR) as BusinessRuleType[]).map((v) => ({
    value: v,
    label: typeMeta(t, v).label,
  }));

/**
 * The four postures, and what separates them.
 *
 * The test is "would another number also have been defensible?". BR-8's
 * 20-character floor could have been 25, so it is a knob; BR-10's neutral 0.5
 * for an unmeasurable dimension could not have been 0.6 without claiming
 * something the code never measured, so it is the algorithm's definition. Each
 * rule already carries its own note saying which side it fell on — what was
 * missing was what the categories mean, so the note read as a verdict with no
 * standard behind it.
 */
const CONFIG_COLOR: Record<BusinessRuleConfigStatus, string> = {
  migrated: 'green',
  hybrid: 'yellow',
  'in-code': 'gray',
  invariant: 'blue',
  unknown: 'gray',
};

const configMeta = (t: TranslateFn, status: BusinessRuleConfigStatus) => ({
  label: t(`br.config.${status}` as MessageKey),
  hint: t(`br.config.${status}Hint` as MessageKey),
  color: CONFIG_COLOR[status],
});

/**
 * Which runtime knobs a rule owns, from the registry rather than from prose.
 *
 * A rule badged "tunable" used to name its key in a free-text config note and
 * stop there — you read the key, went to another screen with no search, and
 * hunted through the groups. The registry already records which rules each key
 * serves (`br`), and the CI guardrail already requires every knob to be
 * documented by one, so the link exists in the data; it was only missing from
 * the screen.
 */
const KNOBS_BY_RULE: Record<string, string[]> = {};
for (const [key, entry] of Object.entries(CONFIG_REGISTRY)) {
  for (const id of (entry as { br?: readonly string[] }).br ?? []) {
    (KNOBS_BY_RULE[id] ??= []).push(key);
  }
}

/**
 * One rule as a table row.
 *
 * This was a stack of cards, which reads well for five rules and badly for 85:
 * every rule occupied a screenful, so comparing two of them meant scrolling
 * between them and holding the first in your head. A row per rule makes the
 * catalog scannable — the thing you do with it most is find one.
 *
 * The rule text is clamped rather than truncated with an ellipsis mid-word, and
 * the full text, source and config are all in the edit modal, so nothing is lost
 * — only deferred to the moment you actually want it.
 */
/**
 * PRE-102: the rejection modal.
 *
 * A separate step rather than an inline field, for the same reason the RTM
 * decision is a modal: the note is the point, and a textarea that appears in a
 * table row invites a two-word answer. The submit stays disabled until there is
 * something to read — the API would refuse it anyway, and a disabled button
 * explains why before the click rather than after.
 */
function RejectModal({
  rule,
  onClose,
  onSubmit,
  busy,
}: {
  rule: AdminBusinessRule;
  onClose: () => void;
  onSubmit: (note: string) => void;
  busy: boolean;
}) {
  const t = useT();
  const [note, setNote] = useState(rule.reviewNote ?? '');
  const missing = note.trim().length === 0;
  return (
    <Modal opened onClose={onClose} size="lg" title={t('br.rejectTitle', { id: rule.id })}>
      <Stack gap="md">
        <Text size="sm">{rule.rule}</Text>
        <Textarea
          label={t('br.rejectReason')}
          description={t('br.rejectReasonHint')}
          minRows={3}
          autosize
          value={note}
          onChange={(e) => {
            setNote(e.currentTarget.value);
          }}
          error={missing ? t('br.reasonRequired') : null}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            color="red"
            disabled={missing || busy}
            onClick={() => {
              onSubmit(note.trim());
            }}
          >
            {t('br.reject')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RuleRow({
  rule,
  onReview,
  onReject,
  reviewing,
}: {
  rule: AdminBusinessRule;
  onReview: (reviewed: boolean) => void;
  onReject: () => void;
  reviewing: boolean;
}) {
  const t = useT();
  const cfg = configMeta(t, rule.configStatus);
  const type = typeMeta(t, rule.type);
  // PRE-126: the review was given to a sentence. When the sentence has moved on,
  // the tick is not current — it would vouch for an invariant nobody read.
  const stale = rule.reviewedAt != null && rule.reviewedRule !== rule.rule;
  // PRE-102: a rejection goes stale the same way a confirmation does — it was
  // given to a sentence, and a rewritten sentence has not been rejected.
  const rejected = rule.reviewVerdict === 'rejected' && !stale;
  return (
    <Table.Tr>
      <Table.Td>
        <Text fw={700} size="sm" ff="monospace">
          {rule.id}
        </Text>
      </Table.Td>
      <Table.Td>
        <Tooltip label={type.hint} withArrow multiline w={280}>
          {/* The label must never be clipped: `product` and `platform` share a
              prefix, so an ellipsised badge renders both as "p…" and the column
              stops carrying any information at all. */}
          <Badge
            variant="light"
            color={type.color}
            size="xs"
            styles={{ root: { maxWidth: 'none' }, label: { overflow: 'visible' } }}
          >
            {type.label}
          </Badge>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        {/* PRE-126: the panel's only write on a rule. The sentence arrives with
            the deploy; what a person adds here is that they have read it. A stale
            tick is drawn unticked with a reason rather than left looking current. */}
        <Stack gap={4} align="flex-start">
          {/* PRE-102: a rejection is not an unticked box. Unticking says "I have
              not decided"; this says "I read it and it is wrong", so it is drawn
              as its own state with the reason attached. */}
          {rejected ? (
            <>
              <Badge variant="light" color="red" size="xs">
                {t('br.rejected')}
              </Badge>
              {rule.reviewNote && (
                <Text size="xs" c="red" style={{ whiteSpace: 'pre-wrap' }}>
                  {rule.reviewNote}
                </Text>
              )}
            </>
          ) : (
            <Checkbox
              size="sm"
              label={t('br.iReviewedIt')}
              checked={rule.reviewedAt != null && !stale}
              disabled={reviewing}
              onChange={(event) => {
                onReview(event.currentTarget.checked);
              }}
            />
          )}
          {stale && (
            <Text size="xs" c="orange">
              {t('br.textChanged')}
            </Text>
          )}
          {rule.reviewedAt != null && !stale && rule.reviewedBy && (
            <Text size="xs" c="dimmed">
              {rule.reviewedBy} · {rule.reviewedAt.slice(0, 10)}
            </Text>
          )}
          {rejected ? (
            <Button
              size="compact-xs"
              variant="subtle"
              disabled={reviewing}
              onClick={() => {
                onReview(false);
              }}
            >
              {t('br.undoDecision')}
            </Button>
          ) : (
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              disabled={reviewing}
              onClick={onReject}
            >
              {t('br.reject')}
            </Button>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color="grape" size="xs">
          {rule.area}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color={PRIORITY_COLOR[rule.priority] ?? 'gray'} size="xs">
          {rule.priority}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={3}>
          {rule.rule}
        </Text>
        {rule.source && (
          <Text c="dimmed" size="xs" mt={4} lineClamp={1}>
            <Code>{rule.source}</Code>
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {rule.config ? (
          <Stack gap={4} align="flex-start">
            {/* Both halves: what the posture means, then why THIS rule got it.
                The rule's own note read as a verdict with no standard behind it
                when the category itself was never explained. */}
            <Tooltip
              label={
                <>
                  <strong>{cfg.label}</strong> — {cfg.hint}
                  <br />
                  <br />
                  {rule.config}
                </>
              }
              withArrow
              multiline
              w={340}
            >
              <Badge variant="light" color={cfg.color} size="xs">
                {cfg.label}
              </Badge>
            </Tooltip>
            {/* The knob itself, one click away. "Tunable" that does not say where
                is a pointer to nowhere. */}
            {(KNOBS_BY_RULE[rule.id] ?? []).map((key) => (
              <Badge
                key={key}
                component={Link}
                to={`/config?key=${encodeURIComponent(key)}`}
                variant="outline"
                color="blue"
                size="xs"
                style={{ cursor: 'pointer', textTransform: 'none' }}
                styles={{ root: { maxWidth: 'none' }, label: { overflow: 'visible' } }}
              >
                {key}
              </Badge>
            ))}
          </Stack>
        ) : (
          <Text c="dimmed" size="xs">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {/* PRE-125: the rule text is authored in the repo; the type decision
            beside it is still made here, which is the whole split. */}
        <Group gap={4} wrap="nowrap">
          <SeededLock file={SEEDED_FILES.businessRules} what={t('br.ruleText')} />
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * PRE-190: Business Rules — Prepzio's product/domain invariants (`BR-*`), read
 * and edited straight from D1.
 *
 * This used to render `docs/business-rules.md`, bundled into this app at build
 * time, which made it a read-only viewer of a file only an engineer could change
 * and — since the admin deploy filter never watched `docs/**` — often a viewer of
 * a stale copy. The catalog is product data, so the product owner edits it here.
 *
 * Ids are never reused. Deleting a rule retires its number permanently, because
 * `// BR-<n>` comments in code and `trace.br` anchors in the blueprint still name
 * it, and recycling the number would quietly reattribute them.
 */
export function BusinessRulesScreen() {
  const t = useT();
  // `?q=` is how the Config screen hands you back the rule a knob serves.
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [area, setArea] = useState<string | null>(null);
  const [type, setType] = useState<BusinessRuleType | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  /** Show only rules whose type nobody has decided yet — unset or still a guess. */
  /**
   * Decided or not — both directions, not just "show me the pending ones".
   *
   * A one-way toggle answers "what is left to do" and nothing else. Reviewing
   * 85 rules also means going back over what you already settled, and there was
   * no way to ask for that.
   */
  const [decision, setDecision] = useState<string | null>(null);

  const { data, isPending, error } = useBusinessRules();
  const review = useReviewBusinessRule();
  // PRE-102: the rule whose rejection is being written, if any.
  const [rejecting, setRejecting] = useState<AdminBusinessRule | null>(null);

  const rules = useMemo(() => data?.rules ?? [], [data]);
  const areas = data?.areas ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rules.filter((r) => {
      if (area && r.area !== area) return false;
      if (type && r.type !== type) return false;
      if (priority && r.priority !== priority) return false;
      const reviewed = r.reviewedAt != null && r.reviewedRule === r.rule;
      if (decision === 'reviewed' && !reviewed) return false;
      if (decision === 'pending' && reviewed) return false;
      // PRE-102: a rejection is the thing on this screen most worth being able
      // to list — it is an open claim that the catalog is wrong.
      if (decision === 'rejected' && !(reviewed && r.reviewVerdict === 'rejected')) return false;
      if (!q) return true;
      return (
        r.id.toLowerCase().includes(q) ||
        r.rule.toLowerCase().includes(q) ||
        r.source.toLowerCase().includes(q) ||
        r.area.toLowerCase().includes(q)
      );
    });
  }, [rules, query, area, type, priority, decision]);

  /** Distinct priorities present, so the filter offers only what exists. */
  const priorities = useMemo(() => [...new Set(rules.map((r) => r.priority))].sort(), [rules]);

  /** The split of the whole catalog, and — the number that matters — how much of
   *  it nobody has read against reality yet. A review is stale once the sentence
   *  it was given has changed, so it counts as unread. */
  const counts = useMemo(
    () => ({
      product: rules.filter((r) => r.type === 'product').length,
      platform: rules.filter((r) => r.type === 'platform').length,
      unreviewed: rules.filter((r) => r.reviewedAt == null || r.reviewedRule !== r.rule).length,
      rejected: rules.filter((r) => r.reviewVerdict === 'rejected' && r.reviewedRule === r.rule)
        .length,
    }),
    [rules],
  );

  return (
    <Stack gap="md">
      <Group gap={6} align="center">
        <Text c="dimmed" size="sm">
          {t('br.intro')}
        </Text>
        <InfoTip label={t('br.infoTip')} />
      </Group>

      {error && (
        <Alert color="red" title={t('br.loadFailed')}>
          {errorMessage(error)}
        </Alert>
      )}

      <Group align="flex-end" wrap="wrap">
        <TextInput
          label={t('common.search')}
          placeholder={t('br.searchPlaceholder')}
          leftSection={<IconSearch size={16} />}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 'min(280px, 100%)' }}
        />
        <Select
          label={t('br.type')}
          placeholder={t('br.allTypes')}
          value={type}
          onChange={(v) => setType(v as BusinessRuleType | null)}
          clearable
          data={typeOptions(t)}
          style={{ width: 160 }}
        />
        <Select
          label={t('br.process')}
          placeholder={t('br.allProcesses')}
          value={area}
          onChange={setArea}
          clearable
          searchable
          data={areas.map((a) => ({ value: a, label: a }))}
          style={{ width: 200 }}
        />
        <Select
          label={t('br.priority')}
          placeholder={t('common.all')}
          value={priority}
          onChange={setPriority}
          clearable
          data={priorities.map((p) => ({ value: p, label: p }))}
          style={{ width: 120 }}
        />
        <Select
          label={t('br.review')}
          placeholder={t('common.all')}
          value={decision}
          onChange={setDecision}
          clearable
          data={[
            { value: 'reviewed', label: t('br.reviewedByMe') },
            { value: 'pending', label: t('br.awaitingReading') },
            { value: 'rejected', label: t('br.rejectedByMe') },
          ]}
          style={{ width: 200 }}
        />
        {/* A new rule is a new sentence, and sentences are authored in the repo. */}
        <SeededLock file={SEEDED_FILES.businessRules} what={t('br.addingRule')} />
      </Group>

      {isPending ? (
        <Loader size="sm" />
      ) : (
        <>
          <Group gap={12} align="center">
            <Text c="dimmed" size="sm">
              {t('br.summary', {
                shown: filtered.length,
                total: rules.length,
                product: counts.product,
                platform: counts.platform,
              })}
            </Text>
            {/* PRE-126: the primary action on this screen. The rule text arrives
                with the deploy and the machine buttons are gone, so what is left
                to do here is read rules — and that number should not be a
                footnote under two controls nobody presses. */}
            {counts.unreviewed > 0 && (
              <Button
                size="compact-xs"
                variant={decision === 'pending' ? 'filled' : 'light'}
                color="orange"
                onClick={() => {
                  setDecision((v) => (v === 'pending' ? null : 'pending'));
                }}
              >
                {t('br.awaitingReview', { count: counts.unreviewed })}
              </Button>
            )}
            {counts.rejected > 0 && (
              <Button
                size="compact-xs"
                variant={decision === 'rejected' ? 'filled' : 'light'}
                color="red"
                onClick={() => {
                  setDecision((v) => (v === 'rejected' ? null : 'rejected'));
                }}
              >
                {t('br.rejectedCount', { count: counts.rejected })}
              </Button>
            )}
          </Group>

          {filtered.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t('br.noMatch')}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={900}>
              <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={80}>{t('rtm.colId')}</Table.Th>
                    <Table.Th w={120}>{t('br.type')}</Table.Th>
                    <Table.Th w={150}>{t('br.colDecision')}</Table.Th>
                    <Table.Th w={120}>{t('br.process')}</Table.Th>
                    <Table.Th w={80}>{t('br.priority')}</Table.Th>
                    <Table.Th>{t('br.colRule')}</Table.Th>
                    <Table.Th w={140}>{t('br.colConfig')}</Table.Th>
                    <Table.Th w={80} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filtered.map((r) => (
                    <RuleRow
                      key={r.id}
                      rule={r}
                      onReview={(reviewed) => {
                        review.mutate({ id: r.id, reviewed });
                      }}
                      onReject={() => {
                        setRejecting(r);
                      }}
                      reviewing={review.isPending}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </>
      )}

      {rejecting && (
        <RejectModal
          rule={rejecting}
          busy={review.isPending}
          onClose={() => {
            setRejecting(null);
          }}
          onSubmit={(note) => {
            review.mutate(
              { id: rejecting.id, reviewed: true, verdict: 'rejected', note },
              {
                onSuccess: () => {
                  setRejecting(null);
                },
              },
            );
          }}
        />
      )}
    </Stack>
  );
}
